import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Menu, MenuItem, Checkbox, Button, Link } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CalendarEventDto } from '../../types/calendar';
import { AddEventButtons } from '../common/AddEventButtons';

const renderWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/\S+)/g;
  const parts = text.split(urlRegex);

  const shortenUrl = (url: string, maxLength = 30) => {
    if (url.length <= maxLength) {
      return url;
    }
    const start = url.substring(0, 20);
    const end = url.substring(url.length - 7);
    return `${start}...${end}`;
  };

  return (
    <>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <Link href={part} target="_blank" rel="noopener noreferrer" key={index} sx={{ wordBreak: 'break-all' }} title={part}>
              {shortenUrl(part)}
            </Link>
          );
        }
        return part;
      })}
    </>
  );
};

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '24px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

const TopSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '16px',
  width: '100%',
}));

const TitleRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 0,
  width: '100%',
}));

const CommentLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
  // height: '41px', // Removed fixed height
}));

const CommentContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
}));

interface ThoughtItemProps {
  feeling: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
}

const ThoughtItem: React.FC<ThoughtItemProps> = ({ feeling, onEdit, onDelete, onToggleComplete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = feeling.actions.includes('edit');
  const canDelete = feeling.actions.includes('delete');
  const canComplete = feeling.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(feeling);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(feeling.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(feeling.id, e.target.checked);
  };

  const formattedTime = new Date(feeling.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  const displayText = feeling.title;

  return (
    <CommentLayout>
      <Checkbox checked={feeling.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <CommentContent>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
        <Typography component="div" variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px', wordBreak: 'break-word' }}>
          {renderWithLinks(displayText)}
        </Typography>
      </CommentContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </CommentLayout>
  );
};

interface FeelingsWidgetProps {
  feelings: CalendarEventDto[];
  onAddFeeling?: () => void;
  onEditFeeling: (event: CalendarEventDto) => void;
  onDeleteFeeling: (eventId: number) => void;
  onToggleFeelingComplete?: (eventId: number, isCompleted: boolean) => void;
}

export const FeelingsWidget: React.FC<FeelingsWidgetProps> = ({ feelings, onAddFeeling, onEditFeeling, onDeleteFeeling, onToggleFeelingComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleComplete = (eventId: number, isCompleted: boolean) => {
    if (onToggleFeelingComplete) {
      onToggleFeelingComplete(eventId, isCompleted);
    }
  };

  const displayedFeelings = isExpanded ? feelings : feelings.slice(0, 3);
  const remainingFeelingsCount = feelings.length - displayedFeelings.length;

  return (
    <StyledPaper>
      <TopSection>
        <TitleRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ width: '18px', height: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'SF Pro Display', fontWeight: 800, fontSize: '12.828px', color: '#41AEE4' }}>
                􀆿
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
              Думки
            </Typography>
          </Box>
        </TitleRow>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          {displayedFeelings.length > 0 ? (
            displayedFeelings.map((feeling) => (
              <ThoughtItem
                key={feeling.id}
                feeling={feeling}
                onEdit={onEditFeeling}
                onDelete={onDeleteFeeling}
                onToggleComplete={handleToggleComplete}
              />
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Поки що немає доданих думок.
            </Typography>
          )}
          {feelings.length > 3 && (
            <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
              {isExpanded ? 'Показати менше' : `Показати ще ${remainingFeelingsCount}...`}
            </Button>
          )}
        </Box>
      </TopSection>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          Додати думки:
        </Typography>
        <AddEventButtons
          onAddText={onAddFeeling}
          showTextButton={true}
          showVoiceButton={false}
          showPhotoButton={false}
          buttonColor="#41AEE4"
        />
      </Box>
    </StyledPaper>
  );
};
